/**
 * FoodItemPage — full 22-section public profile for a food item.
 *
 * Route: /food-pulse/food/:slug
 *
 * The 22 sections are rendered as an anchor-linked table of contents
 * followed by expandable / collapsible cards so the page stays
 * scannable. Everything is null-tolerant — sections whose data the AI
 * couldn't fill are hidden.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ExternalLink, Sparkles, Leaf, Utensils,
  QrCode, ScanLine, AlertTriangle, ShieldCheck, Award,
  Copy, Check, Heart,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import NearbyPricesBlock from "@/components/NearbyPricesBlock";
import RelatedArticlesStrip from "@/components/RelatedArticlesStrip";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_MAP = [
  ["basic",      "Basic Information"],
  ["names",      "Alternate Names"],
  ["images",     "Gallery"],
  ["health",     "Health Benefits"],
  ["nutrition",  "Nutrition"],
  ["consumption","Consumption"],
  ["cultivation","Cultivation"],
  ["production", "Global Production"],
  ["harvest",    "Harvesting"],
  ["byproducts", "By-products"],
  ["storage",    "Storage"],
  ["recipes",    "Recipes"],
  ["safety",     "Food Safety"],
  ["environment","Environmental Impact"],
  ["economic",   "Economic Info"],
  ["culture",    "Cultural Importance"],
  ["facts",      "Interesting Facts"],
  ["faqs",       "FAQs"],
  ["refs",       "References"],
  ["links",      "Website Links"],
  ["videos",     "Videos"],
  ["meta",       "Related Foods"],
];

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 rounded-2xl border p-5 sm:p-6 bg-white" style={{ borderColor: "#B8D3A1" }} data-testid={`food-section-${id}`}>
    <h2 className="font-display text-2xl mb-3" style={{ color: "#1A3B1A" }}>{title}</h2>
    <div className="text-sm leading-relaxed" style={{ color: "#3E5A3E" }}>{children}</div>
  </section>
);

const KV = ({ label, value }) => (value === undefined || value === null || value === "" ? null : (
  <div className="flex flex-col">
    <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">{label}</span>
    <span className="text-sm mt-0.5">{String(value)}</span>
  </div>
));

function pill(text, i) {
  return <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded-full mr-1 mb-1 inline-block" style={{ background: "#DCEAD1", color: "#2F6B2F" }}>{text}</span>;
}

export default function FoodItemPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [showAllRecipes, setShowAllRecipes] = useState(false);
  const [linkedRecipes, setLinkedRecipes] = useState({ primary: [], related: [] });
  const [linkedArticles, setLinkedArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-items/${slug}`);
        if (cancelled) return;
        setF(res.data);
        document.title = `${res.data.common_name} — Food Pulse`;
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? "not_found" : "network");
      }
    })();
    // Cross-links — fire independently so slow requests don't block the main render.
    axios.get(`${API}/api/food-items/${slug}/recipes`, { params: { limit: 12 } })
      .then((r) => { if (!cancelled) setLinkedRecipes(r.data || { primary: [], related: [] }); })
      .catch(() => {});
    axios.get(`${API}/api/food-items/${slug}/articles`, { params: { limit: 6 } })
      .then((r) => { if (!cancelled) setLinkedArticles(r.data?.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const sections = useMemo(() => {
    if (!f) return [];
    const has = (v) => v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);
    return [
      { id: "basic",      title: "Basic Information", show: true },
      { id: "names",      title: "Alternate Names",   show: has(f.alternate_names) },
      { id: "images",     title: "Gallery",           show: (f.images || []).length > 1 },
      { id: "health",     title: "Health Benefits",   show: has(f.health_benefits) },
      { id: "nutrition",  title: "Nutrition",         show: has(f.nutrition) },
      { id: "consumption",title: "Consumption",       show: has(f.consumption) },
      { id: "cultivation",title: "Cultivation",       show: has(f.cultivation) },
      { id: "production", title: "Global Production", show: has(f.global_production) },
      { id: "harvest",    title: "Harvesting",        show: has(f.harvesting) },
      { id: "byproducts", title: "By-products",       show: has(f.byproducts) },
      { id: "storage",    title: "Storage",           show: has(f.storage) },
      { id: "recipes",    title: "Recipes",           show: has(f.recipes) },
      { id: "linked-recipes", title: "Cook it — recipes",
        show: (linkedRecipes.primary.length + linkedRecipes.related.length) > 0 },
      { id: "articles",   title: "Read more",
        show: linkedArticles.length > 0 },
      { id: "safety",     title: "Food Safety",       show: has(f.food_safety) },
      { id: "environment",title: "Environmental Impact", show: has(f.environmental_impact) },
      { id: "economic",   title: "Economic Info",     show: has(f.economic_info) },
      { id: "culture",    title: "Cultural Importance", show: has(f.cultural_importance) },
      { id: "facts",      title: "Interesting Facts", show: (f.interesting_facts || []).length > 0 },
      { id: "faqs",       title: "FAQs",              show: (f.faqs || []).length > 0 },
      { id: "refs",       title: "References",        show: (f.references || []).length > 0 },
      { id: "links",      title: "Website Links",     show: (f.website_links || []).length > 0 },
      { id: "videos",     title: "Videos",            show: (f.video_links || []).length > 0 },
      { id: "meta",       title: "Related Foods",     show: has(f.ai_metadata?.related_foods) || has(f.ai_metadata?.similar_foods) },
    ].filter((s) => s.show);
  }, [f, linkedRecipes, linkedArticles]);

  if (error === "not_found") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="food-item-not-found">
        <Leaf className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <div className="font-display text-2xl mb-1">We haven't profiled this food yet.</div>
        <div className="text-sm opacity-70 mb-4">If you'd like to see it in the database, ask an admin to generate a profile.</div>
        <Link to="/food-pulse/food" className="inline-flex items-center gap-1 text-sm underline">
          <ArrowLeft className="w-3 h-3" /> Back to directory
        </Link>
      </div>
    );
  }
  if (!f) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-sm opacity-70" data-testid="food-item-loading">Loading…</div>;
  }

  const cover = (f.images || [])[0];
  const recipes = f.recipes || [];
  const shownRecipes = showAllRecipes ? recipes : recipes.slice(0, 6);

  return (
    <div className="min-h-screen" style={{ background: "#F7FBF4" }} data-testid={`food-item-page-${slug}`}>
      <SeoHead
        section="food-pulse"
        title={f.ai_metadata?.seo_title || `${f.common_name} — Food Pulse Profile`}
        description={f.ai_metadata?.seo_description || f.description || undefined}
      />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: "#DCEAD1", background: "linear-gradient(180deg,#DCEAD1 0%,#F7FBF4 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
          <div>
            <Link to="/food-pulse/food" className="inline-flex items-center gap-1 text-xs mb-3 opacity-70 hover:opacity-100" data-testid="food-item-back">
              <ArrowLeft className="w-3 h-3" /> Back to Food Pulse directory
            </Link>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#2F6B2F" }}>
              {f.category?.replace(/-/g, " ") || "food"}
              {f.food_family ? ` · ${f.food_family}` : ""}
            </div>
            <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl mt-1" style={{ color: "#1A3B1A" }} data-testid="food-item-h1">{f.common_name}</h1>
            {f.scientific_name && <div className="text-base italic mt-1 opacity-70">{f.scientific_name}</div>}
            {f.description && <p className="mt-4 text-sm sm:text-base max-w-2xl leading-relaxed" style={{ color: "#3E5A3E" }}>{f.description}</p>}
            <button
              onClick={() => nav("/food-pulse/lists/wishlist/new", {
                state: { product: {
                  slug: f.slug, common_name: f.common_name,
                  hero_image: cover?.url, category: f.category,
                }}
              })}
              className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow"
              style={{ background: "#0F3D2E" }}
              data-testid="food-item-add-wish"
            >
              <Heart className="w-4 h-4" /> Add to Wishlist
            </button>
            {f.content_open && (
              <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full"
                   style={{ background: "#F0F7EA", color: "#2F6B2F", border: "1px solid #B8D3A1" }}
                   data-testid="food-item-open-badge"
                   title="This profile is an editorial draft — we welcome corrections and additions.">
                <span>✎</span> Draft profile · <span className="opacity-80">open to community additions</span>
              </div>
            )}
          </div>
          {cover?.url && (
            <img src={cover.url} alt={cover.caption || f.common_name} className="rounded-2xl object-cover w-full aspect-[4/3] shadow-sm" />
          )}
        </div>
      </section>

      {/* ── Product Characteristics · Identifier strip ─────────── */}
      <IdentifierStrip characteristics={f.characteristics} apiBase={API} slug={slug} />

      {/* ── Nearby prices & offers (Phase 1 — Smart Shopping) ─── */}
      <NearbyPricesBlock productSlug={slug} productName={f.common_name} />

      {/* Table of contents */}
      <nav className="max-w-6xl mx-auto px-4 py-6" data-testid="food-item-toc">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">In this profile</div>
        <div className="flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="text-xs px-2.5 py-1 rounded-full border hover:bg-white" style={{ borderColor: "#B8D3A1", background: "#fff", color: "#2F6B2F" }} data-testid={`food-toc-${s.id}`}>
              {s.title}
            </a>
          ))}
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 pb-16 space-y-4">
        {/* Basic info */}
        <Section id="basic" title="Basic Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <KV label="Common name" value={f.common_name} />
            <KV label="Scientific name" value={f.scientific_name} />
            <KV label="Category" value={f.category} />
            <KV label="Food family" value={f.food_family} />
            <KV label="Botanical classification" value={f.botanical_classification} />
            <KV label="Variety" value={f.variety} />
            <KV label="USDA classification" value={f.usda_class} />
          </div>
        </Section>

        {/* Alternate names */}
        {f.alternate_names && Object.keys(f.alternate_names).length > 0 && (
          <Section id="names" title="Alternate Names">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(f.alternate_names).map(([lang, name]) => (
                <div key={lang} className="text-sm"><span className="opacity-60 uppercase text-[10px] font-mono mr-2">{lang}</span>{name}</div>
              ))}
            </div>
          </Section>
        )}

        {/* Gallery */}
        {(f.images || []).length > 1 && (
          <Section id="images" title="Gallery">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(f.images || []).slice(0, 4).map((img, i) => (
                <figure key={i} className="rounded-xl overflow-hidden bg-black/5">
                  <img src={img.url} alt={img.caption || f.common_name} className="w-full aspect-square object-cover" loading="lazy" />
                  <figcaption className="text-[10px] font-mono opacity-70 p-1">{img.kind || ""} {img.caption ? `· ${img.caption}` : ""}</figcaption>
                </figure>
              ))}
            </div>
          </Section>
        )}

        {/* Health */}
        {f.health_benefits && (
          <Section id="health" title="Health Benefits">
            {f.health_benefits.major?.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Major benefits</div>
                <ul className="list-disc list-inside space-y-0.5">{f.health_benefits.major.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Evidence" value={f.health_benefits.evidence} />
              <KV label="Immune" value={f.health_benefits.immune} />
              <KV label="Heart" value={f.health_benefits.heart} />
              <KV label="Brain" value={f.health_benefits.brain} />
              <KV label="Digestive" value={f.health_benefits.digestive} />
              <KV label="Bone" value={f.health_benefits.bone} />
              <KV label="Skin" value={f.health_benefits.skin} />
              <KV label="Eye" value={f.health_benefits.eye} />
            </div>
            {f.health_benefits.disease_prevention?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Disease prevention</div>
                <div className="flex flex-wrap">{f.health_benefits.disease_prevention.map(pill)}</div>
              </div>
            )}
          </Section>
        )}

        {/* Nutrition */}
        {f.nutrition && (
          <Section id="nutrition" title="Nutrition (per 100 g)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(f.nutrition.per_100g || {}).filter(([, v]) => typeof v !== "object" || v === null).map(([k, v]) => (
                <KV key={k} label={k.replace(/_/g, " ")} value={v} />
              ))}
              <KV label="Glycemic index" value={f.nutrition.glycemic_index} />
              <KV label="Glycemic load" value={f.nutrition.glycemic_load} />
              <KV label="Serving (g)" value={f.nutrition.recommended_serving_g} />
              <KV label="Daily intake" value={f.nutrition.recommended_daily_intake} />
            </div>
            {f.nutrition.suitable_diets && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Suitable diets</div>
                <div className="flex flex-wrap">
                  {Object.entries(f.nutrition.suitable_diets)
                    .filter(([, v]) => v === true)
                    .map(([k], i) => pill(k.replace(/_/g, " "), i))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Consumption */}
        {f.consumption && (
          <Section id="consumption" title="Consumption">
            <div className="grid sm:grid-cols-3 gap-3">
              <KV label="Taste" value={f.consumption.taste} />
              <KV label="Texture" value={f.consumption.texture} />
              <KV label="Aroma" value={f.consumption.aroma} />
            </div>
            {f.consumption.methods?.length > 0 && (
              <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Methods</div><div className="flex flex-wrap">{f.consumption.methods.map(pill)}</div></div>
            )}
            {f.consumption.culinary_uses?.length > 0 && (
              <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Culinary uses</div><ul className="list-disc list-inside">{f.consumption.culinary_uses.map((c, i) => <li key={i}>{c}</li>)}</ul></div>
            )}
          </Section>
        )}

        {/* Cultivation */}
        {f.cultivation && (
          <Section id="cultivation" title="Cultivation">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Countries" value={(f.cultivation.countries || []).join(", ")} />
              <KV label="Regions" value={(f.cultivation.regions || []).join(", ")} />
              <KV label="Climate" value={f.cultivation.climate} />
              <KV label="Temperature (°C)" value={f.cultivation.temperature_c} />
              <KV label="Rainfall (mm)" value={f.cultivation.rainfall_mm} />
              <KV label="Soil" value={f.cultivation.soil} />
              <KV label="Growing season" value={f.cultivation.growing_season} />
              <KV label="Irrigation" value={f.cultivation.irrigation} />
              <KV label="Fertilizer" value={f.cultivation.fertilizer} />
              <KV label="Organic" value={f.cultivation.organic} />
              <KV label="Greenhouse" value={f.cultivation.greenhouse} />
              <KV label="Home gardening" value={f.cultivation.home_gardening} />
            </div>
            {(f.cultivation.common_pests || []).length > 0 && <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Common pests</div><div className="flex flex-wrap">{f.cultivation.common_pests.map(pill)}</div></div>}
            {(f.cultivation.common_diseases || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Common diseases</div><div className="flex flex-wrap">{f.cultivation.common_diseases.map(pill)}</div></div>}
            {(f.cultivation.sustainable_practices || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Sustainable practices</div><ul className="list-disc list-inside">{f.cultivation.sustainable_practices.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          </Section>
        )}

        {/* Production */}
        {f.global_production && (
          <Section id="production" title="Global Production">
            {(f.global_production.top_producers || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Top producers</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {f.global_production.top_producers.map((tp, i) => (
                    <div key={i} className="rounded-lg border p-2" style={{ borderColor: "#DCEAD1" }}>
                      <div className="font-semibold text-sm">{tp.country}</div>
                      <div className="text-xs opacity-70">
                        {tp.share_pct != null ? `${tp.share_pct}%` : ""}
                        {tp.annual_tonnes != null ? ` · ${tp.annual_tonnes.toLocaleString()} t/yr` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Exporters" value={(f.global_production.exporters || []).join(", ")} />
              <KV label="Importers" value={(f.global_production.importers || []).join(", ")} />
              <KV label="Seasonal availability" value={f.global_production.seasonal_availability} />
              <KV label="Economic importance" value={f.global_production.economic_importance} />
              <KV label="Market trends" value={f.global_production.market_trends} />
            </div>
          </Section>
        )}

        {/* Harvest */}
        {f.harvesting && (
          <Section id="harvest" title="Harvesting">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Season" value={f.harvesting.season} />
              <KV label="Methods" value={(f.harvesting.methods || []).join(", ")} />
              <KV label="Grading" value={f.harvesting.grading} />
              <KV label="Packaging" value={f.harvesting.packaging} />
              <KV label="Transportation" value={f.harvesting.transportation} />
              <KV label="Shelf life after harvest" value={f.harvesting.shelf_life_after_harvest} />
            </div>
          </Section>
        )}

        {/* Byproducts */}
        {(f.byproducts || []).length > 0 && (
          <Section id="byproducts" title="By-products">
            <ul className="space-y-2">
              {f.byproducts.map((b, i) => (
                <li key={i}><span className="font-semibold">{b.name}</span> — <span className="opacity-80">{b.description}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {/* Storage */}
        {f.storage && (
          <Section id="storage" title="Storage">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Temperature (°C)" value={f.storage.temperature_c} />
              <KV label="Humidity (%)" value={f.storage.humidity_pct} />
              <KV label="Refrigeration" value={f.storage.refrigeration} />
              <KV label="Freezing" value={f.storage.freezing} />
              <KV label="Shelf life" value={f.storage.shelf_life} />
              <KV label="Controlled atmosphere" value={f.storage.controlled_atmosphere} />
              <KV label="Safety notes" value={f.storage.safety_notes} />
            </div>
            {(f.storage.preservation || []).length > 0 && <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Preservation</div><div className="flex flex-wrap">{f.storage.preservation.map(pill)}</div></div>}
          </Section>
        )}

        {/* Recipes */}
        {(f.recipes || []).length > 0 && (
          <Section id="recipes" title={`Recipes (${f.recipes.length})`}>
            <div className="grid gap-3 sm:grid-cols-2">
              {shownRecipes.map((r, i) => (
                <article key={i} className="rounded-xl border p-3" style={{ borderColor: "#DCEAD1" }} data-testid={`food-recipe-${i}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{r.country} · {r.difficulty}</div>
                  <div className="font-display text-base mt-0.5"><Utensils className="w-3 h-3 inline mr-1 opacity-60" />{r.name}</div>
                  <p className="text-xs mt-1 opacity-80">{r.description}</p>
                  <div className="text-[10px] font-mono opacity-60 mt-2">
                    Prep {r.prep_time_min}m · Cook {r.cook_time_min}m · Serves {r.servings}
                  </div>
                </article>
              ))}
            </div>
            {recipes.length > 6 && (
              <button onClick={() => setShowAllRecipes(!showAllRecipes)} className="mt-4 text-xs underline" data-testid="food-recipes-toggle">
                {showAllRecipes ? "Show fewer recipes" : `Show all ${recipes.length} recipes`}
              </button>
            )}
          </Section>
        )}

        {/* Linked recipes from the standalone recipes collection */}
        {(linkedRecipes.primary.length + linkedRecipes.related.length) > 0 && (
          <Section id="linked-recipes"
                   title={`Cook it · ${linkedRecipes.primary.length + linkedRecipes.related.length} recipe${(linkedRecipes.primary.length + linkedRecipes.related.length) === 1 ? "" : "s"}`}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...linkedRecipes.primary, ...linkedRecipes.related].slice(0, 12).map((r) => (
                <Link key={r.slug || r.id}
                      to={`/food-pulse/recipes/${r.slug || r.id}`}
                      className="rounded-xl border overflow-hidden bg-white hover:-translate-y-0.5 hover:shadow-md transition-all block"
                      style={{ borderColor: "#DCEAD1" }}
                      data-testid={`linked-recipe-${r.slug || r.id}`}>
                  {r.cover_photo && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={r.cover_photo} alt={r.title}
                           className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-3">
                    {(r.cuisine || r.country || r.difficulty) && (
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                        {[r.cuisine, r.country, r.difficulty].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="font-display text-base mt-0.5 line-clamp-2"><Utensils className="w-3 h-3 inline mr-1 opacity-60" />{r.title}</div>
                    {r.short_description && (
                      <p className="text-xs mt-1 opacity-70 line-clamp-2">{r.short_description}</p>
                    )}
                    {(r.total_time_min || r.servings) && (
                      <div className="text-[10px] font-mono opacity-60 mt-2">
                        {r.total_time_min && `${r.total_time_min}m`}
                        {r.total_time_min && r.servings && " · "}
                        {r.servings && `Serves ${r.servings}`}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            {(linkedRecipes.primary.length + linkedRecipes.related.length) > 12 && (
              <Link to={`/food-pulse/recipes?ingredient=${f.slug}`}
                    className="mt-4 inline-block text-xs underline"
                    data-testid="linked-recipes-see-all">
                See all {linkedRecipes.primary.length + linkedRecipes.related.length} recipes →
              </Link>
            )}
          </Section>
        )}

        {/* Linked long-form articles from the Ghost catalogue */}
        {linkedArticles.length > 0 && (
          <Section id="articles" title={`Read more · ${linkedArticles.length} article${linkedArticles.length === 1 ? "" : "s"}`}>
            <div className="grid gap-3 sm:grid-cols-2">
              {linkedArticles.map((a) => (
                <Link key={a.slug} to={`/food-pulse/articles/${a.slug}`}
                      className="flex gap-3 rounded-xl border p-3 bg-white hover:-translate-y-0.5 hover:shadow-md transition-all"
                      style={{ borderColor: "#DCEAD1" }}
                      data-testid={`linked-article-${a.slug}`}>
                  {a.cover_image && (
                    <img src={a.cover_image} alt={a.title}
                         className="w-24 h-24 rounded-lg object-cover flex-none border"
                         style={{ borderColor: "#DCEAD1" }} loading="lazy" />
                  )}
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{a.category || a.tag}</div>
                    <div className="font-display text-base line-clamp-2">{a.title}</div>
                    {a.excerpt && <p className="text-xs opacity-70 mt-1 line-clamp-2">{a.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* Safety */}
        {f.food_safety && (
          <Section id="safety" title="Food Safety">
            {(f.food_safety.allergies || []).length > 0 && <div className="mb-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Allergies</div><div className="flex flex-wrap">{f.food_safety.allergies.map(pill)}</div></div>}
            {(f.food_safety.toxic_parts || []).length > 0 && <div className="mb-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Toxic parts</div><div className="flex flex-wrap">{f.food_safety.toxic_parts.map(pill)}</div></div>}
            {(f.food_safety.precautions || []).length > 0 && <div className="mb-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Precautions</div><ul className="list-disc list-inside">{f.food_safety.precautions.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
            <KV label="Safe handling" value={f.food_safety.safe_handling} />
            <KV label="Storage safety" value={f.food_safety.storage_safety} />
            {(f.food_safety.foodborne_illness_risks || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Foodborne illness risks</div><ul className="list-disc list-inside">{f.food_safety.foodborne_illness_risks.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
          </Section>
        )}

        {/* Environment */}
        {f.environmental_impact && (
          <Section id="environment" title="Environmental Impact">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Water footprint (L/kg)" value={f.environmental_impact.water_footprint_l_per_kg} />
              <KV label="Carbon footprint (kg CO₂e/kg)" value={f.environmental_impact.carbon_footprint_kg_co2e_per_kg} />
              <KV label="Sustainability rating" value={f.environmental_impact.sustainability_rating} />
              <KV label="Organic production" value={f.environmental_impact.organic_production} />
              <KV label="Regenerative agriculture" value={f.environmental_impact.regenerative_agriculture} />
              <KV label="Biodiversity impact" value={f.environmental_impact.biodiversity_impact} />
            </div>
          </Section>
        )}

        {/* Economic */}
        {f.economic_info && (
          <Section id="economic" title="Economic Info">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Retail (USD/kg)" value={f.economic_info.retail_price_range_usd_per_kg} />
              <KV label="Wholesale (USD/kg)" value={f.economic_info.wholesale_price_range_usd_per_kg} />
              <KV label="Seasonal price trends" value={f.economic_info.seasonal_price_trends} />
              <KV label="Global trade value" value={f.economic_info.global_trade_value_usd} />
            </div>
          </Section>
        )}

        {/* Culture */}
        {f.cultural_importance && (
          <Section id="culture" title="Cultural Importance">
            <div className="grid sm:grid-cols-2 gap-3">
              <KV label="Historical" value={f.cultural_importance.historical} />
              <KV label="Religious" value={f.cultural_importance.religious} />
            </div>
            {(f.cultural_importance.festivals || []).length > 0 && <div className="mt-3"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Festivals</div><div className="flex flex-wrap">{f.cultural_importance.festivals.map(pill)}</div></div>}
            {(f.cultural_importance.ceremonies || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Ceremonies</div><div className="flex flex-wrap">{f.cultural_importance.ceremonies.map(pill)}</div></div>}
            {(f.cultural_importance.indigenous_uses || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Indigenous uses</div><ul className="list-disc list-inside">{f.cultural_importance.indigenous_uses.map((u, i) => <li key={i}>{u}</li>)}</ul></div>}
            {(f.cultural_importance.folklore || []).length > 0 && <div className="mt-2"><div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Folklore</div><ul className="list-disc list-inside">{f.cultural_importance.folklore.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          </Section>
        )}

        {/* Facts */}
        {(f.interesting_facts || []).length > 0 && (
          <Section id="facts" title="Interesting Facts">
            <ul className="list-disc list-inside space-y-1">{f.interesting_facts.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </Section>
        )}

        {/* FAQs */}
        {(f.faqs || []).length > 0 && (
          <Section id="faqs" title="Frequently Asked Questions">
            <div className="space-y-3">
              {f.faqs.map((qa, i) => (
                <details key={i} className="rounded-xl border p-3" style={{ borderColor: "#DCEAD1" }}>
                  <summary className="cursor-pointer font-semibold">{qa.q}</summary>
                  <p className="mt-2 text-sm opacity-80">{qa.a}</p>
                </details>
              ))}
            </div>
          </Section>
        )}

        {/* References */}
        {(f.references || []).length > 0 && (
          <Section id="refs" title="References">
            <ul className="space-y-1">{f.references.map((r, i) => (
              <li key={i}>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm underline inline-flex items-center gap-1">
                  {r.title} <ExternalLink className="w-3 h-3" />
                </a>
                {r.publisher && <span className="text-xs opacity-60 ml-2">· {r.publisher}</span>}
              </li>
            ))}</ul>
          </Section>
        )}

        {/* Links */}
        {(f.website_links || []).length > 0 && (
          <Section id="links" title="Website Links">
            <ul className="space-y-1">{f.website_links.map((r, i) => (
              <li key={i}><a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm underline inline-flex items-center gap-1">{r.title} <ExternalLink className="w-3 h-3" /></a></li>
            ))}</ul>
          </Section>
        )}

        {/* Videos */}
        {(f.video_links || []).length > 0 && (
          <Section id="videos" title="Videos">
            <ul className="space-y-1">{f.video_links.map((r, i) => (
              <li key={i} className="text-sm"><span className="opacity-60 text-[10px] font-mono uppercase tracking-widest mr-2">{r.kind}</span><a href={r.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">{r.title} <ExternalLink className="w-3 h-3" /></a></li>
            ))}</ul>
          </Section>
        )}

        {/* AI metadata (related foods) */}
        {(f.ai_metadata?.related_foods?.length > 0 || f.ai_metadata?.similar_foods?.length > 0) && (
          <Section id="meta" title="Related Foods">
            {(f.ai_metadata?.related_foods || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Related</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.ai_metadata.related_foods.map((slug2, i) => (
                    <Link key={i} to={`/food-pulse/food/${slug2}`} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "#B8D3A1", background: "#fff", color: "#2F6B2F" }}>{slug2}</Link>
                  ))}
                </div>
              </div>
            )}
            {(f.ai_metadata?.similar_foods || []).length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Similar</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.ai_metadata.similar_foods.map((slug2, i) => (
                    <Link key={i} to={`/food-pulse/food/${slug2}`} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "#B8D3A1", background: "#fff", color: "#2F6B2F" }}>{slug2}</Link>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* ── Related articles (cross-linking hub) ────────────── */}
        <RelatedArticlesStrip
          foodItemSlug={slug}
          palette={{ accent: "#0F3D2E", bg: "#F0F7EA", line: "#B8D3A1", ink: "#1A3B1A", inkSoft: "#3E5A3E" }}
        />

        {/* Source footer */}
        {f.source && (
          <div className="text-[10px] font-mono opacity-50 mt-4">
            <Sparkles className="inline w-3 h-3 mr-1" />
            Profile source: {f.source}
            {f.updated_at ? ` · updated ${new Date(f.updated_at).toLocaleDateString()}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Product Characteristics · Identifier strip ─────────────────
// Renders under the hero:
//   • Identifier card — barcode strip (or "no barcode" placeholder),
//     QR badge, FoodPulse Product ID, short URL (with copy button)
//   • Dietary chips (vegan / veg / GF / halal / …)
//   • GHS Hazard card (only for products with `food_safety.ghs_hazard`
//     — typically non-food cleaning / automotive / pest-control)
//   • Certification ribbons
function IdentifierStrip({ characteristics, apiBase, slug }) {
  const [copied, setCopied] = useState(false);
  const chars = characteristics || {};
  const ids   = chars.identifiers || {};
  const diet  = chars.dietary     || {};
  const safety = chars.food_safety || {};
  const ghs    = safety.ghs_hazard   || null;
  const certs  = (ids.other_certifications || []).slice();
  const nutrition = chars.nutrition || {};

  const hasIdentifierBlock = ids.foodpulse_product_id || ids.short_url;
  const hasDietary = Object.values(diet).some((v) => v === true);
  const hasCerts   = certs.length > 0 || ids.usda_organic || ids.kosher_id || ids.halal_id || ids.non_gmo_id || ids.fair_trade_id || ids.rainforest_alliance_id;

  // Nothing to render → save a section
  if (!hasIdentifierBlock && !hasDietary && !ghs && !hasCerts) return null;

  const copyShortUrl = async () => {
    if (!ids.short_url) return;
    try {
      await navigator.clipboard.writeText(ids.short_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may not be available */ }
  };

  const dietChips = [
    ["vegan",       "Vegan",         "#166534"],
    ["vegetarian",  "Vegetarian",    "#16A34A"],
    ["pescatarian", "Pescatarian",   "#0891B2"],
    ["keto",        "Keto",          "#7C2D12"],
    ["paleo",       "Paleo",         "#854D0E"],
    ["gluten_free", "Gluten-Free",   "#B45309"],
    ["dairy_free",  "Dairy-Free",    "#374151"],
    ["nut_free",    "Nut-Free",      "#78350F"],
    ["soy_free",    "Soy-Free",      "#059669"],
    ["halal",       "Halal",         "#0369A1"],
    ["kosher",      "Kosher",        "#4338CA"],
    ["jain",        "Jain",          "#DC2626"],
    ["organic",     "Organic",       "#15803D"],
    ["non_gmo",     "Non-GMO",       "#0E7490"],
    ["low_carb",    "Low-Carb",      "#B91C1C"],
    ["high_protein","High-Protein",  "#7C3AED"],
    ["high_fiber",  "High-Fibre",    "#EA580C"],
  ].filter(([k]) => diet[k] === true);

  return (
    <section className="max-w-6xl mx-auto px-4 py-6" data-testid="food-item-identifiers">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
        {/* ── Left column: identifier card ─────────────────────── */}
        {hasIdentifierBlock && (
          <div className="rounded-2xl border bg-white p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-start"
               style={{ borderColor: "#DCEAD1" }} data-testid="fp-identifier-card">
            {/* QR badge */}
            {ids.qr_code_url && (
              <div className="flex-none w-32 rounded-xl border p-2 text-center"
                   style={{ borderColor: "#B8D3A1", background: "#F7FBF4" }}>
                <img src={`${apiBase}${ids.qr_code_url}`}
                     alt="QR code · scan or tap"
                     className="w-full aspect-square rounded"
                     data-testid="fp-qr-image" />
                <div className="text-[9px] font-mono mt-1 opacity-60">Scan or share</div>
              </div>
            )}

            <div className="flex-1 min-w-0 space-y-2">
              {/* FP ID + short URL row */}
              <div className="flex flex-wrap gap-3 items-baseline">
                <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">FoodPulse ID</span>
                <span className="font-mono text-sm font-semibold" style={{ color: "#1A3B1A" }}
                      data-testid="fp-product-id">{ids.foodpulse_product_id}</span>
              </div>
              {ids.short_url && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">Short URL</span>
                  <a href={ids.short_url} target="_blank" rel="noreferrer"
                     className="text-xs font-mono underline"
                     style={{ color: "#2F6B2F" }}
                     data-testid="fp-short-url">{ids.short_url}</a>
                  <button type="button" onClick={copyShortUrl}
                          className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                          data-testid="fp-short-url-copy">
                    {copied ? <><Check className="w-3 h-3" /> copied</> : <><Copy className="w-3 h-3" /> copy</>}
                  </button>
                </div>
              )}

              {/* Barcode strip */}
              {ids.barcode_image_url ? (
                <div className="mt-2 flex flex-wrap gap-3 items-center">
                  <img src={`${apiBase}${ids.barcode_image_url}`}
                       alt={`Barcode · ${ids.gtin || ""}`}
                       className="rounded border" style={{ background: "#fff", borderColor: "#DCEAD1", maxHeight: 60 }}
                       data-testid="fp-barcode-image" />
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                    {ids.gtin_type} · <span className="opacity-80">{ids.gtin}</span>
                  </div>
                </div>
              ) : ids.plu ? (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1"
                     style={{ background: "#FFF7ED", color: "#B45309", border: "1px solid #FDBA74" }}
                     data-testid="fp-plu-badge">
                  <ScanLine className="w-3 h-3" />
                  <span className="text-[10px] font-mono uppercase tracking-widest">PLU · {ids.plu}</span>
                  {ids.plu_organic && <span className="text-[10px] opacity-70">· organic {ids.plu_organic}</span>}
                </div>
              ) : (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest opacity-60">
                  No barcode on file · <Link to="/food-pulse/label-scan" className="underline" style={{ color: "#2F6B2F" }}>scan one in</Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Right column: dietary chips + certifications ────── */}
        {(hasDietary || hasCerts) && (
          <div className="space-y-3 sm:min-w-[220px] max-w-md">
            {hasDietary && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1.5">Dietary</div>
                <div className="flex flex-wrap gap-1.5" data-testid="fp-diet-chips">
                  {dietChips.map(([k, label, color]) => (
                    <span key={k}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border"
                          style={{ color, borderColor: `${color}55`, background: "#fff" }}
                          data-testid={`fp-diet-${k}`}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {hasCerts && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1.5">Certifications</div>
                <div className="flex flex-wrap gap-1.5" data-testid="fp-cert-ribbons">
                  {ids.usda_organic && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }}>
                      <Award className="w-3 h-3" /> USDA Organic
                    </span>
                  )}
                  {ids.non_gmo_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#F0FDFA", color: "#0E7490" }}>
                      <Award className="w-3 h-3" /> Non-GMO
                    </span>
                  )}
                  {ids.fair_trade_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#FEF3C7", color: "#854D0E" }}>
                      <Award className="w-3 h-3" /> Fair Trade
                    </span>
                  )}
                  {ids.kosher_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                      <ShieldCheck className="w-3 h-3" /> Kosher
                    </span>
                  )}
                  {ids.halal_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#EFF6FF", color: "#0369A1" }}>
                      <ShieldCheck className="w-3 h-3" /> Halal
                    </span>
                  )}
                  {ids.rainforest_alliance_id && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }}>
                      <Award className="w-3 h-3" /> Rainforest Alliance
                    </span>
                  )}
                  {certs.filter((c) => {
                    const l = String(c).toLowerCase();
                    return !l.includes("organic") && !l.includes("non-gmo") && !l.includes("fair trade") && !l.includes("kosher") && !l.includes("halal") && !l.includes("rainforest");
                  }).map((c, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                          style={{ background: "#F5F3FF", color: "#6D28D9" }}>
                      <Award className="w-3 h-3" /> {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* GHS Hazard card — full-width warning banner when present */}
      {ghs && (
        <div className="mt-4 rounded-2xl border-2 p-4 flex flex-col md:flex-row gap-3 items-start"
             style={{ borderColor: "#DC2626", background: "#FEF2F2" }} data-testid="fp-ghs-hazard-card">
          <div className="flex-none w-10 h-10 rounded-full inline-flex items-center justify-center"
               style={{ background: "#DC2626", color: "#fff" }}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-base" style={{ color: "#7F1D1D" }}>
              {ghs.signal_word || "Warning"} · {ghs.classification || "GHS Hazard"}
            </div>
            {Array.isArray(ghs.hazard_statements) && ghs.hazard_statements.length > 0 && (
              <ul className="mt-1.5 text-sm space-y-0.5" style={{ color: "#7F1D1D" }}>
                {ghs.hazard_statements.map((h, i) => (<li key={i}>• {h}</li>))}
              </ul>
            )}
            {Array.isArray(ghs.precautionary_statements) && ghs.precautionary_statements.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Precautions</div>
                <ul className="mt-0.5 text-xs space-y-0.5" style={{ color: "#7F1D1D" }}>
                  {ghs.precautionary_statements.map((p, i) => (<li key={i}>• {p}</li>))}
                </ul>
              </div>
            )}
            {Array.isArray(ghs.pictograms) && ghs.pictograms.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {ghs.pictograms.map((p, i) => (
                  <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded border"
                        style={{ borderColor: "#DC2626", color: "#7F1D1D", background: "#fff" }}>
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
